import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Sparkles, ShoppingBag, Check, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useFavorites, PERFUME_SIZES } from "@/context/FavoritesContext";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";

import { API, BACKEND_URL } from "../apiConfig";
import LOGO_URL from "../img/sapphire.svg";

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ─── Favorite Item Component ──────────────────────────────────
const FavoriteItem = ({ perfume, onRemove, onSizeChange, checked, onCheck }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const placeholderImage = "https://images.unsplash.com/photo-1618436623662-6043c7cb58df?w=200&h=250&fit=crop";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={`bg-white rounded-2xl shadow-sm border p-4 flex gap-4 transition-colors duration-200 ${
        checked ? "border-rose-300 ring-1 ring-rose-200" : "border-slate-100"
      }`}
      data-testid={`favorite-item-${perfume.id}`}
    >
      {/* Checkbox */}
      <button
        onClick={() => onCheck(perfume.id)}
        className={`flex-shrink-0 w-6 h-6 mt-1 rounded-lg border-2 flex items-center justify-center transition-all ${
          checked ? "bg-rose-500 border-rose-500" : "border-slate-300 hover:border-rose-300"
        }`}
        data-testid={`check-item-${perfume.id}`}
      >
        {checked && (
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>

      {/* Image */}
      <Link to={`/perfume/${perfume.id}`} className="flex-shrink-0">
        <div className="w-20 h-24 bg-[#FFFFFF] rounded-xl overflow-hidden flex items-center justify-center">
          <img
            src={imageError ? placeholderImage : (perfume.imagen_url || placeholderImage)}
            alt={perfume.nombre}
            className={`max-h-full max-w-full object-contain transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => { setImageError(true); setImageLoaded(true); }}
          />
        </div>
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <Link to={`/perfume/${perfume.id}`}>
          <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
            {perfume.marca}
          </p>
          <h3 className="font-serif text-base text-slate-900 leading-tight line-clamp-2 hover:text-slate-700 transition-colors">
            {perfume.nombre}
          </h3>
        </Link>
        {perfume.tipo_marca && (
          <Badge variant="outline" className="mt-1.5 text-xs rounded-full pointer-events-none">
            {perfume.tipo_marca}
          </Badge>
        )}

        {/* Size Selector */}
        <div className="mt-2">
          <p className="text-xs text-slate-500 mb-1">Elige la presentación en ml</p>
          <Select value={perfume.selectedSize} onValueChange={(value) => onSizeChange(perfume.id, value)}>
            <SelectTrigger className="w-full h-9 text-sm rounded-full" data-testid={`size-select-${perfume.id}`}>
              <SelectValue placeholder="Tamaño" />
            </SelectTrigger>
            <SelectContent>
              {PERFUME_SIZES.map((size) => (
                <SelectItem key={size.id} value={size.id}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Remove */}
      <button
        onClick={() => onRemove(perfume.id)}
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-colors"
        data-testid={`remove-favorite-${perfume.id}`}
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </motion.div>
  );
};

// ─── Main Wishlist Component ──────────────────────────────────
const Wishlist = () => {
  const { favorites, removeFavorite, removeBulk, updateSize, clearFavorites } = useFavorites();
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [clientName, setClientName] = useState("");
  const [sending, setSending] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false); // Nuevo estado

  // Default: all selected. Re-sync when favorites change.
  useEffect(() => {
    setCheckedItems(new Set(favorites.map((f) => f.id)));
  }, [favorites]);

  const isEmpty = favorites.length === 0;
  const checkedCount = checkedItems.size;
  const allChecked = checkedCount === favorites.length && favorites.length > 0;
  

  const toggleCheck = (id) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) {
      setCheckedItems(new Set());
    } else {
      setCheckedItems(new Set(favorites.map((f) => f.id)));
    }
  };

  const handleDeleteSelected = () => {
    if (checkedItems.size === 0) return;
    const ids = [...checkedItems];
    if (removeBulk) {
      removeBulk(ids);
    } else {
      ids.forEach((id) => removeFavorite(id));
    }
    setCheckedItems(new Set());
    toast.success(`${ids.length} perfume(s) eliminados del pedido`);
  };

  const selectedPerfumes = favorites.filter((f) => checkedItems.has(f.id));

  const buildOrderMessage = () => {
    const name = clientName.trim() || "Cliente";
    const header = `Hola! Soy ${name}. Me gustaría hacer el siguiente pedido:\n\n`;
    const items = selectedPerfumes.map((f, i) => {
      const size = PERFUME_SIZES.find((s) => s.id === f.selectedSize);
      return `${i + 1}. ${f.nombre} - ${f.marca} (${size?.label || "60ml"})`;
    }).join("\n");
    return header + items + "\n\nGracias!";
  };

  const getWhatsAppOrderLink = () => {
    return `https://wa.me/525547630396?text=${encodeURIComponent(buildOrderMessage())}`;
  };

  const handleSendOrder = async () => {
    if (selectedPerfumes.length === 0) {
      toast.error("Selecciona al menos un perfume para tu pedido");
      return;
    }
    if (!clientName.trim()) {
      toast.error("Escribe tu nombre para el pedido");
      return;
    }

    setSending(true);
   try {
      const orderItems = selectedPerfumes.map((f) => {
        const size = PERFUME_SIZES.find((s) => s.id === f.selectedSize);
        return {
          perfume_id: f.id,
          nombre: f.nombre,
          marca: f.marca,
          tipo_marca: f.tipo_marca || "",
          size: size?.label || "60ml",
        };
      });

      await axios.post(`${API}/orders`, {
        client_name: clientName.trim(),
        items: orderItems,
      });

      // Limpieza y estado de éxito
      clearFavorites();
      setClientName("");
      setOrderCompleted(true); // <--- Activamos el mensaje de éxito

      window.open(getWhatsAppOrderLink(), "_blank");
      toast.success("Pedido registrado con éxito");

    } catch (err) {
      console.error("Error saving order:", err);
      // Still open WhatsApp even if save fails
      window.open(getWhatsAppOrderLink(), "_blank");
      toast.info("Se abrió WhatsApp, pero no se pudo guardar el registro.");
    } finally {
      setSending(false);
    }
  };

    return (
    <div className="min-h-screen bg-[#FFF5F7]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 border-b border-white/40 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">Catálogo</span>
            </Link>
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <img src={LOGO_URL} alt="Sapphire Spell" className="h-[72px] object-contain" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-slate-900 tracking-tight flex items-center gap-3">
            <ShoppingBag className="w-8 h-8 text-rose-500" strokeWidth={1.5} />
            Mi Lista de Deseos
          </h1>
          <p className="text-slate-500 mt-2">
            {isEmpty
              ? "Aún no has agregado perfumes a tu lista"
              : `${favorites.length} ${favorites.length === 1 ? 'perfume' : 'perfumes'} en tu lista`}
          </p>
        </div>

       {isEmpty ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16 px-4">
            <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${orderCompleted ? 'bg-emerald-50' : 'bg-rose-50'}`}>
              {orderCompleted ? (
                <Check className="w-12 h-12 text-emerald-500" strokeWidth={2} />
              ) : (
                <ShoppingBag className="w-12 h-12 text-rose-300" strokeWidth={1.5} />
              )}
            </div>
            
            <h2 className="font-serif text-3xl text-slate-900 mb-3">
              {orderCompleted ? "¡Pedido enviado con éxito!" : "Tu pedido está vacío"}
            </h2>
            
            <p className="text-slate-500 text-lg mb-8 max-w-sm mx-auto">
              {orderCompleted 
                ? "Tu solicitud ha sido registrada. Puedes crear un nuevo pedido regresando al catálogo."
                : "Explora el catálogo y agrega los perfumes que quieras pedir"}
            </p>
            
            <Link to="/">
              <Button className="rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all" data-testid="explore-catalog-btn">
                Regresar al catálogo
              </Button>
            </Link>
          </motion.div>
        ) : (
          <>
            {/* Select all + bulk delete */}
            <div className="flex items-center justify-between mb-4">
              <Button variant={allChecked ? "secondary" : "outline"} size="sm" className="rounded-full text-sm" onClick={toggleAll} data-testid="select-all-btn">
                <Check className="w-4 h-4 mr-1.5" />
                {allChecked ? "Deseleccionar todo" : "Seleccionar todo"}
              </Button>
              <div className="flex items-center gap-3">
                {checkedCount > 0 && (
                  <span className="text-sm text-rose-600 font-medium">{checkedCount} seleccionado{checkedCount > 1 ? "s" : ""}</span>
                )}
                {checkedCount > 0 && (
                  <Button variant="ghost" size="sm" className="rounded-full text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar seleccionados
                  </Button>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3 mb-8">
              <AnimatePresence mode="popLayout">
                {favorites.map((perfume) => (
                  <FavoriteItem
                    key={perfume.id}
                    perfume={perfume}
                    onRemove={removeFavorite}
                    onSizeChange={updateSize}
                    checked={checkedItems.has(perfume.id)}
                    onCheck={toggleCheck}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Order Section */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-5">
              <h2 className="font-serif text-xl text-slate-900">Hacer pedido</h2>

              {/* Client Name */}
              <div>
                <label htmlFor="client-name" className="text-sm font-medium text-slate-700 block mb-2">Tu nombre</label>
                <Input
                  id="client-name"
                  placeholder="Escribe tu nombre..."
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="rounded-full"
                  data-testid="client-name-input"
                />
              </div>

              {/* Order Summary */}
              {checkedCount > 0 && (
                <div className="bg-slate-50 rounded-2xl p-4 text-sm">
                  <p className="font-medium text-slate-700 mb-2">Tu pedido incluye:</p>
                  <ul className="space-y-1 text-slate-600">
                    {selectedPerfumes.map((f, i) => {
                      const size = PERFUME_SIZES.find((s) => s.id === f.selectedSize);
                      return (
                        <li key={f.id} className="flex justify-between">
                          <span className="truncate mr-2">{i + 1}. {f.nombre} ({f.marca})</span>
                          <span className="flex-shrink-0 font-medium">{size?.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* WhatsApp Order Button */}
              <Button
                className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full py-6 text-lg font-medium shadow-lg shadow-[#25D366]/30 transition-colors disabled:opacity-50"
                size="lg"
                onClick={handleSendOrder}
                disabled={sending || checkedCount === 0}
                data-testid="whatsapp-order-btn"
              >
                <WhatsAppIcon className="w-6 h-6 mr-3" />
                {sending ? "Enviando..." : `Enviar pedido (${checkedCount})`}
              </Button>

              <p className="text-center text-sm text-slate-500">
                El pedido se guardará y se abrirá WhatsApp con tu lista
              </p>

              {/* Clear */}
              <div className="pt-4 border-t border-slate-100">
                <button onClick={clearFavorites} className="w-full text-center text-sm text-slate-500 hover:text-rose-500 transition-colors" data-testid="clear-wishlist-btn">
                  Vaciar pedido
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Wishlist;
